import { useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, login } from '../lib/rpc';
import { meQueryKey } from '../lib/queries';
import { t } from '../messages';
import { PasswordField } from './PasswordField';

/**
 * L'écran de connexion. Il appelle réellement `POST /api/auth/login` — même route, même garde
 * CSRF, même cookie `HttpOnly` que le reste de la plateforme : aucun raccourci de démonstration.
 *
 * Les messages d'erreur affichés sont **ceux de l'API** (« Identifiant ou mot de passe
 * incorrect… », « Compte verrouillé, réessayez dans une minute. ») : ils sont déjà écrits en
 * français, pour un élève, et disent quoi faire. Les réécrire ici les ferait diverger
 * silencieusement le jour où l'API changera.
 */
export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (identifier.trim() === '' || password === '') {
      setError(t('login.errorEmpty'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await login(identifier.trim(), password);
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
      await navigate('/', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('desk.errorTitle'));
      setBusy(false);
    }
  }

  return (
    <div className="desk">
      <form
        className="login-card"
        onSubmit={(event) => {
          void onSubmit(event);
        }}
      >
        <div className="login-head">
          <span className="login-brand" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 10 10" fill="currentColor" focusable="false">
              <path d="M5 1.1 8.9 5 5 8.9 1.1 5z" />
            </svg>
          </span>
          <h1 className="login-title">{t('product.name')}</h1>
          <p className="login-subtitle">{t('login.subtitle')}</p>
        </div>

        <label className="ndrc-field">
          <span className="ndrc-label">{t('login.identifier')}</span>
          <input
            className="ndrc-input"
            name="identifier"
            autoComplete="username"
            autoFocus
            value={identifier}
            onChange={(event) => {
              setIdentifier(event.target.value);
            }}
          />
          <span className="ndrc-hint">{t('login.identifierHint')}</span>
        </label>

        <PasswordField
          label={t('login.password')}
          name="password"
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
        />

        {error === null ? null : <p className="ndrc-error">{error}</p>}

        <button type="submit" className="ndrc-button ndrc-button--primary" disabled={busy}>
          {busy ? t('login.submitting') : t('login.submit')}
        </button>
      </form>
    </div>
  );
}
